import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderRevenueDashboard = () => {
  const { sessionToken } = useUnifiedAuth();
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [comparisonTier, setComparisonTier] = useState(null); // null = auto (next tier)

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/provider/revenue-stats`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setRevenueData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!revenueData) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-600">Unable to load revenue data</p>
      </div>
    );
  }

  const { 
    commission_rate, 
    currency, 
    current_month, 
    monthly_breakdown, 
    all_time_stats, 
    payout_schedule 
  } = revenueData;
  
  // Extract payout data from new structure
  const next_payout_date = payout_schedule?.next_payout_date;
  const next_payout_day = payout_schedule?.next_payout_day;
  const pending_payout = payout_schedule?.pending_amount || 0;
  const payout_after_next = payout_schedule?.payout_after_next;

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Pending Payout */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Pending Payout</h3>
            <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-3xl font-bold mb-1">{pending_payout.toFixed(3)} {currency}</p>
          <p className="text-xs opacity-90">Next payout: {next_payout_day || new Date(next_payout_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
          <p className="text-xs opacity-75 mt-1">Bi-monthly (15th & last day)</p>
        </div>

        {/* Current Month Revenue */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">This Month</h3>
            <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-3xl font-bold mb-1">{current_month.total_revenue?.toFixed(3) || 0} {currency}</p>
          <p className="text-xs opacity-90">{current_month.appointments || 0} appointments</p>
        </div>

        {/* Commission Rate */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">Current Commission Rate</h3>
            <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-3xl font-bold mb-1">{commission_rate}%</p>
          <p className="text-xs opacity-90">{revenueData.tier_name} Plan</p>
        </div>

        {/* All Time Earnings */}
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium opacity-90">All Time Earnings</h3>
            <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <p className="text-3xl font-bold mb-1">{all_time_stats.total_earnings.toFixed(3)} {currency}</p>
          <p className="text-xs opacity-90">{all_time_stats.total_appointments} bookings</p>
        </div>
      </div>

      {/* Payout Schedule Section */}
      {payout_schedule && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-lg p-6 border border-blue-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                💰 Payout Schedule
              </h3>
              <p className="text-sm text-gray-600 mb-4">{payout_schedule.frequency}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Next Payout */}
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Next Payout</p>
                  <p className="text-2xl font-bold text-blue-600">{next_payout_day}</p>
                  <p className="text-sm text-gray-600 mt-2">
                    Amount: <span className="font-semibold text-green-600">{pending_payout.toFixed(3)} {currency}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {payout_schedule.pending_appointments_count} completed appointment{payout_schedule.pending_appointments_count !== 1 ? 's' : ''}
                  </p>
                </div>
                
                {/* Following Payout */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Following Payout</p>
                  <p className="text-xl font-bold text-gray-700">
                    {new Date(payout_after_next).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="text-xs text-gray-500 mt-3">
                    💡 {payout_schedule.note}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Smart Tier Upgrade Marketing */}
      {(() => {
        // Launch Promotion: 50% off for 6 months
        const LAUNCH_PROMOTION = {
          active: false,
          discount: 0.50,
          duration: '6 months',
          tiers: ['Basic', 'Pro']
        };
        
        const getPromotionalPrice = (tier, originalPrice) => {
          if (LAUNCH_PROMOTION.active && LAUNCH_PROMOTION.tiers.includes(tier)) {
            return originalPrice * (1 - LAUNCH_PROMOTION.discount);
          }
          return originalPrice;
        };
        
        const tierData = {
          'Free': { 
            commission: 15, 
            price: 0, 
            originalPrice: 0,
            next: 'Basic' 
          },
          'Basic': { 
            commission: 12.5, 
            price: getPromotionalPrice('Basic', 20), 
            originalPrice: 20,
            next: 'Pro' 
          },
          'Pro': { 
            commission: 10, 
            price: getPromotionalPrice('Pro', 60), 
            originalPrice: 60,
            next: 'Enterprise' 
          },
          'Enterprise': { 
            commission: 5, 
            price: null, 
            originalPrice: null,
            isCustomPricing: true,
            next: null 
          }
        };
        
        const currentTier = revenueData.tier_name || 'Free';
        const currentData = tierData[currentTier];
        const monthlyRevenue = current_month?.total_revenue || 0;
        const currentEarnings = current_month?.provider_earnings || 0;
        
        console.log('🎯 Tier Upgrade Check:', {
          currentTier,
          monthlyRevenue,
          currentEarnings,
          hasCurrentData: !!currentData,
          nextTier: currentData?.next
        });
        
        // Calculate projections for all tiers
        const projections = Object.entries(tierData).map(([tier, data]) => {
          const commission = monthlyRevenue * (data.commission / 100);
          const earnings = monthlyRevenue - commission;
          const netEarnings = earnings - (data.price || 0);
          const savingsVsCurrent = netEarnings - (currentEarnings - (currentData.price || 0));
          
          return {
            tier,
            commission: data.commission,
            price: data.price,
            isCustomPricing: !!data.isCustomPricing,
            earnings: earnings,
            netEarnings: netEarnings,
            savingsVsCurrent: savingsVsCurrent,
            isCurrent: tier === currentTier
          };
        });
        
        // Determine which tier to compare
        // Custom-priced tiers can't be compared on price — that's a sales conversation
        const tierToCompare = comparisonTier
          || (tierData[currentData.next]?.isCustomPricing ? null : currentData.next);
        const comparisonTierProjection = projections.find(p => p.tier === tierToCompare);
        const shouldShowUpgrade = comparisonTierProjection && monthlyRevenue > 50;
        
        console.log('🎯 Upgrade Decision:', {
          currentTier,
          tierToCompare,
          hasComparisonTier: !!comparisonTierProjection,
          savings: comparisonTierProjection?.savingsVsCurrent,
          shouldShow: shouldShowUpgrade,
          monthlyRevenue
        });
        
        if (!shouldShowUpgrade) return null;
        
        const breakEvenRevenue = tierToCompare && tierData[tierToCompare].price ? 
          tierData[tierToCompare].price / ((currentData.commission - tierData[tierToCompare].commission) / 100) : 0;
        const isAboveBreakEven = monthlyRevenue > breakEvenRevenue;
        
        // Available tiers for comparison (exclude current tier)
        const availableTiers = Object.keys(tierData).filter(t => t !== currentTier);
        
        return (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-lg p-6 border-2 border-green-300">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-2xl">📈</span>
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-gray-900">
                    {comparisonTierProjection.savingsVsCurrent > 0 ? (
                      <>💡 Increase Your Earnings by {comparisonTierProjection.savingsVsCurrent.toFixed(0)} {currency}/month!</>
                    ) : (
                      <>📊 Compare {currentTier} vs {tierToCompare} Plans</>
                    )}
                  </h3>
                  
                  {/* Tier Selector */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Compare with:</label>
                    <select
                      value={comparisonTier || currentData.next || 'Basic'}
                      onChange={(e) => setComparisonTier(e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {availableTiers.map(tier => (
                        <option key={tier} value={tier}>{tier}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-4">
                  {comparisonTierProjection.savingsVsCurrent > 0 ? (
                    <>Upgrade from <strong>{currentTier}</strong> to <strong>{tierToCompare}</strong> and keep more of what you earn.</>
                  ) : (
                    <>See how <strong>{tierToCompare}</strong> compares to your current <strong>{currentTier}</strong> plan. Upgrade becomes beneficial as your revenue grows.</>
                  )}
                </p>
                
                {/* Comparison Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Current Tier */}
                  <div className="bg-white rounded-lg p-4 border border-gray-300">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">{currentTier} (Current)</span>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                        {currentData.commission}% commission
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly Revenue:</span>
                        <span className="font-semibold">{monthlyRevenue.toFixed(0)} {currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Commission:</span>
                        <span className="text-red-600">-{(monthlyRevenue * currentData.commission / 100).toFixed(0)} {currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subscription:</span>
                        <span className="text-red-600">-{currentData.price} {currency}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-200">
                        <span className="font-semibold">Net Earnings:</span>
                        <span className="font-bold text-gray-900">
                          {(currentEarnings - currentData.price).toFixed(0)} {currency}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Comparison Tier */}
                  <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg p-4 border-2 border-green-400 relative overflow-hidden">
                    {comparisonTierProjection.savingsVsCurrent > 0 && (
                      <div className="absolute top-2 right-2">
                        <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                          RECOMMENDED
                        </span>
                      </div>
                    )}
                    {LAUNCH_PROMOTION.active && LAUNCH_PROMOTION.tiers.includes(tierToCompare) && (
                      <div className="absolute top-10 right-2">
                        <span className="bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded animate-pulse">
                          50% OFF 🎉
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-green-900">{tierToCompare}</span>
                      <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded">
                        {tierData[tierToCompare].commission}% commission
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-green-900">Monthly Revenue:</span>
                        <span className="font-semibold text-green-900">{monthlyRevenue.toFixed(0)} {currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-900">Commission:</span>
                        <span className="text-red-600">-{(monthlyRevenue * tierData[tierToCompare].commission / 100).toFixed(0)} {currency}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-900">Subscription:</span>
                        <div className="text-right">
                          {LAUNCH_PROMOTION.active && LAUNCH_PROMOTION.tiers.includes(tierToCompare) ? (
                            <>
                              <div className="text-red-600">
                                <span className="line-through text-gray-500 text-xs">{tierData[tierToCompare].originalPrice}</span>{' '}
                                <span className="font-bold">-{tierData[tierToCompare].price} {currency}</span>
                              </div>
                              <div className="text-xs text-pink-600">First {LAUNCH_PROMOTION.duration}</div>
                            </>
                          ) : (
                            <span className="text-red-600">
                              {tierData[tierToCompare].isCustomPricing
                                ? 'Custom quote'
                                : `-${tierData[tierToCompare].price} ${currency}`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-green-300">
                        <span className="font-semibold text-green-900">Net Earnings:</span>
                        <span className="font-bold text-green-700">
                          {comparisonTierProjection.netEarnings.toFixed(0)} {currency}
                        </span>
                      </div>
                      {comparisonTierProjection.savingsVsCurrent !== 0 && (
                        <div className="mt-2 pt-2 border-t border-green-300">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-green-900">
                              {comparisonTierProjection.savingsVsCurrent > 0 ? 'Monthly Increase:' : 'Monthly Decrease:'}
                            </span>
                            <span className={`text-lg font-bold ${comparisonTierProjection.savingsVsCurrent > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {comparisonTierProjection.savingsVsCurrent > 0 ? '+' : ''}{comparisonTierProjection.savingsVsCurrent.toFixed(0)} {currency}
                            </span>
                          </div>
                          <div className={`text-xs mt-1 ${comparisonTierProjection.savingsVsCurrent > 0 ? 'text-green-700' : 'text-red-700'}`}>
                            💰 Annual {comparisonTierProjection.savingsVsCurrent > 0 ? 'increase' : 'decrease'}: {comparisonTierProjection.savingsVsCurrent > 0 ? '+' : ''}{(comparisonTierProjection.savingsVsCurrent * 12).toFixed(0)} {currency}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* All Tiers Comparison */}
                <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
                  <p className="text-xs text-gray-500 uppercase font-medium mb-3">Compare All Tiers (Based on Your Current Revenue)</p>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    {projections.map(proj => (
                      <div key={proj.tier} className={`p-2 rounded text-center cursor-pointer transition-all ${
                        proj.isCurrent ? 'bg-blue-100 border border-blue-300' : 
                        proj.tier === tierToCompare ? 'bg-green-100 border-2 border-green-400' :
                        'bg-gray-50 border border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => !proj.isCurrent && setComparisonTier(proj.tier)}>
                        <div className={`font-semibold mb-1 ${proj.isCurrent ? 'text-blue-700' : proj.tier === tierToCompare ? 'text-green-700' : 'text-gray-700'}`}>
                          {proj.tier}
                        </div>
                        <div className="text-gray-600">{proj.commission}% comm.</div>
                        <div className="text-gray-600">
                          {proj.isCustomPricing ? 'Custom quote' : `${proj.price} ${currency}/mo`}
                        </div>
                        <div className="font-bold text-green-600 mt-1">
                          {proj.netEarnings.toFixed(0)} {currency}
                        </div>
                        {!proj.isCurrent && !proj.isCustomPricing && (
                          <div className={`text-xs mt-1 ${proj.savingsVsCurrent > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {proj.savingsVsCurrent > 0 ? '+' : ''}{proj.savingsVsCurrent.toFixed(0)} {currency}
                          </div>
                        )}
                        {!proj.isCurrent && proj.isCustomPricing && (
                          <div className="text-xs mt-1 text-amber-600">Contact sales</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* CTA Button */}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    {comparisonTierProjection.savingsVsCurrent > 0 ? (
                      <span>✅ Upgrading will increase your earnings by {comparisonTierProjection.savingsVsCurrent.toFixed(0)} {currency}/month</span>
                    ) : (
                      <span>ℹ️ Upgrade becomes profitable at {breakEvenRevenue.toFixed(0)} {currency}/month revenue (you're at {monthlyRevenue.toFixed(0)} {currency})</span>
                    )}
                  </div>
                  <button 
                    onClick={() => window.location.href = '/pricing'}
                    className={`px-6 py-3 font-bold rounded-lg transition-colors shadow-lg ${
                      comparisonTierProjection.savingsVsCurrent > 0 
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-600 text-white hover:bg-gray-700'
                    }`}
                  >
                    {comparisonTierProjection.savingsVsCurrent > 0 ? 'Upgrade to' : 'View'} {tierToCompare} →
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Monthly Breakdown */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Monthly Revenue Breakdown</h2>
          <p className="text-sm text-gray-600 mt-1">Last 12 months performance</p>
        </div>

        {monthly_breakdown && monthly_breakdown.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointments</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your Earnings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthly_breakdown.map((month, idx) => {
                  const isCurrentMonth = month.month_key === current_month.month_key;
                  return (
                    <tr key={month.month_key} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${isCurrentMonth ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{month.month}</div>
                            {isCurrentMonth && <div className="text-xs text-green-600">Current</div>}
                          </div>
                        </div>
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-semibold">{month.appointments}</div>
                      <div className="text-xs text-gray-500">bookings</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">{month.total_revenue.toFixed(3)} {currency}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-red-600 font-medium">-{month.commission_amount.toFixed(3)} {currency}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-green-600">{month.provider_earnings.toFixed(3)} {currency}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedMonth(month)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-100">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">TOTAL</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{all_time_stats.total_appointments}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{all_time_stats.total_revenue.toFixed(3)} {currency}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">-{all_time_stats.total_commission.toFixed(3)} {currency}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">{all_time_stats.total_earnings.toFixed(3)} {currency}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-gray-500 text-lg">No revenue data yet</p>
            <p className="text-gray-400 text-sm mt-2">Start accepting appointments to see your earnings here</p>
          </div>
        )}
      </div>

      {/* Month Detail Modal */}
      {selectedMonth && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedMonth.month}</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedMonth.appointments} appointments • {selectedMonth.provider_earnings.toFixed(3)} {currency} earnings</p>
              </div>
              <button
                onClick={() => setSelectedMonth(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Service</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Fee</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {selectedMonth.appointments_list.map((apt, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm text-gray-900">{new Date(apt.date).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{apt.time}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{apt.patient_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{apt.service}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          apt.status === 'completed' ? 'bg-green-100 text-green-800' :
                          apt.status === 'confirmed' || apt.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {apt.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{apt.fee.toFixed(3)} {currency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Billing Information</h4>
            <p className="text-sm text-blue-800">
              • Payouts are processed <strong>bi-monthly on the 14th and 1st of each month</strong><br/>
              • Your current commission rate is <strong>{commission_rate}%</strong> ({revenueData.tier_name} Plan)<br/>
              • Commission rates by tier: Free (15%), Basic (12.5%), Pro (10%), Enterprise (5%)<br/>
              • Upgrade your plan to reduce commission rates and increase earnings<br/>
              • All amounts shown in Kuwaiti Dinar (KWD)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderRevenueDashboard;
